import React, {
  createContext, useContext, useEffect, useState, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { fetchAndActivate, getAll } from 'firebase/remote-config';
import { initializeRemoteConfig } from '../lib/ssr-firbase-client';

const isProduction = process.env.NEXT_PUBLIC_HOME_REMOTE_CONFIG_ENV === 'PROD';

const FirebaseContext = createContext({
  remoteConfig: {},
  loading: true,
  error: null,
  refreshConfig: () => Promise.resolve(),
  getConfigValue: () => null,
  isFeatureEnabled: () => false,
});

export function FirebaseProvider({
  children,
  initialConfig = {},
  user = null,
}) {
  const [remoteConfig, setRemoteConfig] = useState(initialConfig);
  const [loading, setLoading] = useState(!initialConfig || Object.keys(initialConfig).length === 0);
  const [error, setError] = useState(null);

  // Helper function to parse config values (for client-side fetching)
  const parseConfigValue = (value) => {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return value;
    }
  };

  const initializeConfig = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const config = initializeRemoteConfig();
      const activated = await fetchAndActivate(config);
      if (!isProduction) {
        console.log('[Firebase] Remote config activated:', activated);
      }

      const allValues = getAll(config);
      const configObject = {};

      Object.entries(allValues).forEach(([key, value]) => {
        const stringValue = value.asString();
        configObject[key] = parseConfigValue(stringValue);
      });

      if (!isProduction) {
        console.log('[Firebase] Config values:', configObject);
      }

      setRemoteConfig(configObject);
    } catch (err) {
      console.error('[Firebase] Error initializing remote config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount (client-side only)
  // Re-fetch when initialConfig changes (organization switch)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // If we have initialConfig from SSR, use it first
      if (initialConfig && Object.keys(initialConfig).length > 0) {
        // eslint-disable-next-line no-console
        if (!isProduction) {
          console.log('[Firebase] Using SSR config:', initialConfig);
        }

        setRemoteConfig(initialConfig);
        setLoading(false);
      } else {
        // No SSR config, fetch on client
        initializeConfig();
      }
    }
  }, [initializeConfig, initialConfig]);

  // Refresh config manually
  const refreshConfig = useCallback(async () => {
    await initializeConfig();
  }, [initializeConfig]);

  // Helper to get config value
  const getConfigValue = useCallback((key, defaultValue = null) => (remoteConfig[key] !== undefined ? remoteConfig[key] : defaultValue), [remoteConfig]);

  // Helper to check if feature is enabled
  const isFeatureEnabled = useCallback((key) => {
    const value = remoteConfig[key];
    return value === true || value === 'true' || value === '1';
  }, [remoteConfig]);

  // Context value with user-aware config
  const contextValue = useMemo(() => ({
    remoteConfig,
    loading,
    error,
    refreshConfig,
    getConfigValue,
    isFeatureEnabled,
    user, // Include user for conditional feature flags
  }), [remoteConfig, loading, error, refreshConfig, getConfigValue, isFeatureEnabled, user]);

  return (
    <FirebaseContext.Provider value={contextValue}>
      {children}
    </FirebaseContext.Provider>
  );
}

// Custom hook to use Firebase context
export function useFirebase() {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within FirebaseProvider');
  }
  return context;
}

// Custom hook for remote config
export function useRemoteConfig(key, defaultValue = null) {
  const { remoteConfig, loading, getConfigValue } = useFirebase();

  const value = key ? getConfigValue(key, defaultValue) : remoteConfig;

  return {
    value,
    loading,
    config: remoteConfig,
  };
}

FirebaseProvider.propTypes = {
  children: PropTypes.any,
  initialConfig: PropTypes.object,
  user: PropTypes.object,
};

// Custom hook for feature flags
export function useFeatureFlag(flagKey) {
  const { isFeatureEnabled, loading, user } = useFirebase();

  // You can add user-specific logic here
  const isEnabled = useCallback(() => {
    // Example: Enable beta features only for specific users
    if (flagKey === 'feature_flag_beta_features' && user?.email?.endsWith('@admin.com')) {
      return true;
    }
    return isFeatureEnabled(flagKey);
  }, [flagKey, isFeatureEnabled, user]);

  return {
    enabled: isEnabled(),
    loading,
  };
}
